import {IEmailSettings} from './email.interface';

export const MAIL_AUTH_SMTP = 'smtp';
export const MAIL_AUTH_MICROSOFT_365 = 'microsoft365';
export const GALLIX_MICROSOFT_MAIL_MESSAGE = 'gallix-microsoft-mail';

export type TMailAuthType = typeof MAIL_AUTH_SMTP | typeof MAIL_AUTH_MICROSOFT_365;

/**
 * True when Microsoft 365 is the saved sender.
 * A missing authType is Standard.
 */
export function isMicrosoft365Auth(settings: IEmailSettings | null | undefined): boolean {
  return settings?.authType === MAIL_AUTH_MICROSOFT_365;
}

/**
 * True when switching the account choice to Standard should write authType to Firestore.
 * A missing mailer document must not be created by toggling back from Microsoft 365.
 * An existing document, including one with no fields, still records the choice.
 */
export function shouldPersistStandardAuthType(
  settings: IEmailSettings | null | undefined,
  emailKeyedMailerDocumentExists: boolean,
): boolean {
  if (!emailKeyedMailerDocumentExists) {
    return false;
  }
  return settings?.authType === MAIL_AUTH_MICROSOFT_365;
}

/**
 * Label for the account that was used the last time email settings were tested.
 */
export function lastTestAccountLabel(authType: string | null | undefined): string {
  if (authType === MAIL_AUTH_MICROSOFT_365) {
    return 'Microsoft 365';
  }
  if (authType === MAIL_AUTH_SMTP) {
    return 'Standard';
  }
  return '';
}

/**
 * Mailbox address to show while Microsoft 365 is the active sender.
 * A Standard sender address is not treated as a connected mailbox.
 */
export function microsoftMailboxAddress(settings: IEmailSettings | null | undefined): string {
  if (!isMicrosoft365Auth(settings) || settings?.hasMicrosoftRefreshToken !== true) {
    return '';
  }
  return settings.connectedMailbox || settings.email || '';
}

/**
 * Copies mailer settings into app state without `microsoft.refreshToken`.
 * The presence of that token is kept as `hasMicrosoftRefreshToken`.
 */
export function sanitizeMailerSettings(raw: unknown): IEmailSettings {
  if (!raw || typeof raw !== 'object') {
    return {} as IEmailSettings;
  }
  const source: Record<string, unknown> = {...(raw as Record<string, unknown>)};
  const microsoft = source['microsoft'];
  let hasToken = false;
  if (microsoft && typeof microsoft === 'object') {
    const token = (microsoft as Record<string, unknown>)['refreshToken'];
    hasToken = typeof token === 'string' && token.trim().length > 0;
  }
  const alreadyFlagged = source['hasMicrosoftRefreshToken'] === true;
  delete source['microsoft'];
  delete source['hasMicrosoftRefreshToken'];
  delete source['connectedMailbox'];
  const settings = source as unknown as IEmailSettings;
  if (hasToken || alreadyFlagged) {
    settings.hasMicrosoftRefreshToken = true;
  }
  return withConnectedMailbox(settings);
}

/**
 * Settings safe to merge back to Firestore.
 * Client-only flags and the Microsoft token are left untouched on the server.
 */
export function toPersistableMailerSettings(settings: IEmailSettings): IEmailSettings {
  const persistable: IEmailSettings = {...settings};
  delete persistable.hasMicrosoftRefreshToken;
  delete persistable.connectedMailbox;
  delete (persistable as {microsoft?: unknown}).microsoft;
  return persistable;
}

/**
 * Copies Microsoft sign-in from the Firebase-uid document onto the email-keyed settings.
 * authType on the email-keyed document stays as saved. The uid document is not authoritative for it.
 */
export function overlayMicrosoftAccount(base: IEmailSettings, uidSettings: IEmailSettings): IEmailSettings {
  if (base.authType !== MAIL_AUTH_MICROSOFT_365) {
    return withConnectedMailbox(base);
  }
  const hasToken: boolean = uidSettings.hasMicrosoftRefreshToken === true || base.hasMicrosoftRefreshToken === true;
  if (!hasToken) {
    return withConnectedMailbox(base);
  }
  return withConnectedMailbox({
    ...base,
    hasMicrosoftRefreshToken: true,
    connectedMailbox: base.connectedMailbox || uidSettings.connectedMailbox || uidSettings.email || base.email,
  });
}

/**
 * Records a successful Microsoft sign-in in memory.
 * The emailer has already stored the refresh token; this does not read it.
 */
export function applyConnectedMailbox(settings: IEmailSettings, email?: string): IEmailSettings {
  return withConnectedMailbox({
    ...settings,
    authType: MAIL_AUTH_MICROSOFT_365,
    hasMicrosoftRefreshToken: true,
    connectedMailbox: email || settings.connectedMailbox || settings.email,
  });
}

/**
 * Standard is the sender again after the emailer removes the Microsoft connection.
 * SMTP fields already on the document are left as they are.
 */
export function applyDisconnectedAccount(settings: IEmailSettings): IEmailSettings {
  const next: IEmailSettings = {
    ...settings,
    authType: MAIL_AUTH_SMTP,
    hasMicrosoftRefreshToken: false,
  };
  delete next.connectedMailbox;
  return next;
}

/**
 * Plain explanation for a failed email test, with the server message kept as the last line.
 */
export function emailTestFailureCopy(message: string | undefined): {heading: string; details: string[]} {
  const raw = (message || '').trim();
  if (raw.includes('microsoft.refreshToken')) {
    return {
      heading: 'Mailbox sign-in was not found',
      details: [
        'Microsoft 365 is selected on the Gallix login, but that record did not yet have the mailbox sign-in. Standard SMTP settings on that record are left in place.',
        'This is not an Exchange setup check. Save and test again so the sign-in is stored with those settings.',
        raw,
      ],
    };
  }
  return {
    heading: 'The test email was not sent',
    details: [raw || 'No further detail was returned.'],
  };
}

/**
 * Fills the display mailbox when Microsoft 365 is active, and clears it for Standard.
 */
function withConnectedMailbox(settings: IEmailSettings): IEmailSettings {
  if (settings.authType !== MAIL_AUTH_MICROSOFT_365 || settings.hasMicrosoftRefreshToken !== true) {
    const next: IEmailSettings = {...settings};
    delete next.connectedMailbox;
    return next;
  }
  return {
    ...settings,
    connectedMailbox: settings.connectedMailbox || settings.email || '',
  };
}
